import { Button } from 'antd';
import { BookOpen, Plus, ShieldAlert } from 'lucide-react';
import { useState } from 'react';
import { EmptyState } from '../../../shared/components/EmptyState';
import { TableSkeleton } from '../../../shared/components/TableSkeleton';
import { useDriverLedger } from '../hooks/use-driver-ledger';
import { useDriverPermissions } from '../hooks/use-driver-permissions';
import { DriverLedgerEntryDrawer } from './DriverLedgerEntryDrawer';
import { DriverLedgerTable } from './DriverLedgerTable';

interface DriverLedgerTabProps {
  driverId: string;
  onRecorded: () => Promise<void> | void;
}

/** Every movement of cash between the company and this driver, newest first. Entries are never edited: a mistake is a correction row. */
export function DriverLedgerTab({ driverId, onRecorded }: DriverLedgerTabProps) {
  const { canRecordLedger } = useDriverPermissions();
  const { state, list, record, recording, recordError, clearRecordError } = useDriverLedger(driverId, onRecorded);
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);

  function closeDrawer() {
    clearRecordError();
    setIsDrawerOpen(false);
  }

  if (state.status === 'loading') return <TableSkeleton label="Loading driver ledger" />;
  if (state.status === 'error') {
    return <EmptyState icon={ShieldAlert} title="Couldn't load the ledger" description="Something went wrong. Try reloading the page." />;
  }

  const isEmpty = state.page.total === 0 && !list.isFiltered;

  return (
    <section aria-label="Driver ledger" className="flex flex-col gap-3">
      <div className="flex items-center justify-between">
        <h3 className="text-sm font-medium text-fg">Ledger</h3>
        {canRecordLedger && (
          <Button type="primary" icon={<Plus className="size-4" />} onClick={() => setIsDrawerOpen(true)}>
            Record entry
          </Button>
        )}
      </div>
      {isEmpty ? (
        <EmptyState icon={BookOpen} title="No ledger entries yet" description="Advances at dispatch, top-ups and cash returned appear here." />
      ) : (
        <DriverLedgerTable items={state.page.items} total={state.page.total} list={list} />
      )}
      <DriverLedgerEntryDrawer open={isDrawerOpen} error={recordError} submitting={recording} onRecord={record} onClose={closeDrawer} />
    </section>
  );
}
